import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const RevenueDashboard = () => {
  const [timeframe, setTimeframe] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [loyaltyError, setLoyaltyError] = useState(null);

  const fetchData = async (tf) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('sessionToken');
      const res = await fetch(`${API}/api/admin/revenue-dashboard?timeframe=${tf}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load revenue data');
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(timeframe); }, [timeframe]);

  // Points cost sits alongside revenue, but has its own months-based window
  useEffect(() => {
    const fetchLoyalty = async () => {
      setLoyaltyError(null);
      try {
        const token = localStorage.getItem('sessionToken');
        const res = await fetch(`${API}/api/admin/loyalty-report?months=6`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to load points report');
        setLoyalty(await res.json());
      } catch (e) {
        setLoyaltyError(e.message);
      }
    };
    fetchLoyalty();
  }, []);

  const loyaltyTotals = loyalty?.totals || {};
  const loyaltyMonths = loyalty?.months || [];
  const costShare = loyaltyTotals.period_commission_kwd
    ? (loyaltyTotals.period_cost_kwd / loyaltyTotals.period_commission_kwd) * 100
    : 0;

  const totals = data?.totals || {};
  const monthlyTrends = data?.monthly_trends || [];
  const popularServices = data?.popular_services || [];
  const topProviders = data?.top_providers || [];
  const recentBookings = data?.recent_bookings || [];
  const cancellations = data?.cancellations || {};
  const credits = data?.credits || {};
  const topUps = data?.top_ups || {};
  const detailedMonthly = data?.detailed_monthly || [];

  const maxTrendRevenue = Math.max(...monthlyTrends.map(m => m.revenue), 1);

  const metrics = [
    {
      title: 'Total Booking Revenue',
      value: `${(totals.total_booking_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KD`,
      change: totals.revenue_growth ? `${totals.revenue_growth > 0 ? '+' : ''}${totals.revenue_growth}%` : '—',
      trend: (totals.revenue_growth || 0) >= 0 ? 'up' : 'down',
      sub: totals.period_revenue > 0 ? `${totals.period_revenue} KD this ${timeframe}` : null,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-green-600 bg-green-100'
    },
    {
      title: 'Platform Revenue',
      value: `${(totals.platform_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KD`,
      sub: `Commissions: ${(totals.total_commission || 0).toFixed(2)} + Subs: ${(totals.subscription_revenue || 0).toFixed(2)}`,
      change: '',
      trend: 'up',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      color: 'text-blue-600 bg-blue-100'
    },
    {
      title: 'Total Bookings',
      value: (totals.total_bookings || 0).toLocaleString(),
      change: totals.bookings_growth ? `${totals.bookings_growth > 0 ? '+' : ''}${totals.bookings_growth}%` : '—',
      trend: (totals.bookings_growth || 0) >= 0 ? 'up' : 'down',
      sub: totals.period_bookings > 0 ? `${totals.period_bookings} this ${timeframe}` : null,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      color: 'text-purple-600 bg-purple-100'
    },
    {
      title: 'Avg Booking Value',
      value: `${(totals.avg_booking_value || 0).toFixed(2)} KD`,
      change: `${totals.paying_providers || 0} paid subs`,
      trend: 'up',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      color: 'text-amber-600 bg-amber-100'
    }
  ];

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-green-600 border-t-transparent"></div>
          <span className="text-gray-600">Loading revenue data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 font-medium mb-2">Error loading dashboard</p>
          <p className="text-gray-500 text-sm">{error}</p>
          <button onClick={() => fetchData(timeframe)} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="revenue-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold">Revenue Dashboard</h1>
              <p className="text-green-100 mt-2">Real-time platform financial performance</p>
            </div>
            <div className="flex space-x-2">
              {['week', 'month', 'quarter', 'year'].map(period => (
                <button
                  key={period}
                  onClick={() => setTimeframe(period)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                    timeframe === period
                      ? 'bg-white text-green-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  data-testid={`timeframe-${period}`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-200 p-6" data-testid={`metric-${index}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${metric.color}`}>
                  {metric.icon}
                </div>
                {metric.change && (
                  <div className={`text-sm font-medium px-2 py-1 rounded-full ${
                    metric.trend === 'up'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {metric.change}
                  </div>
                )}
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">{metric.value}</div>
              <div className="text-sm text-gray-600">{metric.title}</div>
              {metric.sub && <div className="text-xs text-gray-400 mt-1">{metric.sub}</div>}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Revenue Trend */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Revenue Trend</h2>
                <div className="text-sm text-gray-500">Last 6 months</div>
              </div>
              <div className="space-y-4">
                {monthlyTrends.map((month, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-12 text-sm text-gray-600 font-medium">{month.month}</div>
                    <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-green-400 to-blue-500 h-8 rounded-full flex items-center justify-end pr-3 transition-all duration-500"
                        style={{ width: `${Math.max((month.revenue / maxTrendRevenue) * 100, 5)}%` }}
                      >
                        {month.revenue > 0 && (
                          <span className="text-white text-xs font-medium whitespace-nowrap">
                            {month.revenue.toLocaleString()} KD
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="w-24 text-sm text-gray-500 text-right">
                      {month.bookings} bookings
                    </div>
                  </div>
                ))}
                {monthlyTrends.every(m => m.revenue === 0) && (
                  <p className="text-center text-gray-400 py-4">No revenue data for this period</p>
                )}
              </div>
            </div>

            {/* Top Revenue Services */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Top Revenue Services</h2>
              <div className="space-y-4">
                {popularServices.length > 0 ? popularServices.map((service, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-bold text-blue-600">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{service.name}</div>
                        <div className="text-sm text-gray-600">{service.bookings} bookings</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{service.revenue.toLocaleString()} KD</div>
                      <div className="text-xs text-green-600">Commission: {service.commission.toFixed(2)} KD</div>
                    </div>
                  </div>
                )) : (
                  <p className="text-center text-gray-400 py-4">No service data available</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Live Bookings */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Live Bookings</h3>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-sm text-green-600">Live</span>
                </div>
              </div>
              <div className="space-y-3">
                {recentBookings.length > 0 ? recentBookings.map((booking, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                    <div>
                      <div className="font-medium text-gray-900 text-sm">{booking.service}</div>
                      <div className="text-xs text-gray-600">{booking.provider}</div>
                      <div className="text-xs text-gray-500">{booking.time}</div>
                    </div>
                    <div className="text-green-600 font-semibold text-sm">
                      +{booking.amount} KD
                    </div>
                  </div>
                )) : (
                  <p className="text-center text-gray-400 py-4 text-sm">No recent bookings</p>
                )}
              </div>
            </div>

            {/* Top Revenue Providers */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Revenue Providers</h3>
              <div className="space-y-3">
                {topProviders.length > 0 ? topProviders.map((provider, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-purple-600">
                          {provider.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{provider.name}</div>
                        <div className="text-xs text-gray-600">{provider.bookings} bookings</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900 text-sm">
                        {provider.commission.toFixed(2)} KD
                      </div>
                      <div className="text-xs text-gray-500">commission</div>
                    </div>
                  </div>
                )) : (
                  <p className="text-center text-gray-400 py-4 text-sm">No provider data</p>
                )}
              </div>
            </div>

            {/* Revenue Breakdown */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Breakdown</h3>
              <div className="space-y-3">
                <div className="bg-white/60 rounded-lg p-3 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Commission Revenue</span>
                  <span className="text-sm font-bold text-green-600">{(totals.total_commission || 0).toFixed(2)} KD</span>
                </div>
                <div className="bg-white/60 rounded-lg p-3 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Subscription Revenue</span>
                  <span className="text-sm font-bold text-blue-600">{(totals.subscription_revenue || 0).toFixed(2)} KD</span>
                </div>
                <div className="bg-white/60 rounded-lg p-3 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Paying Providers</span>
                  <span className="text-sm font-bold text-purple-600">{totals.paying_providers || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">
                {totals.total_booking_revenue && totals.total_commission
                  ? ((totals.total_commission / totals.total_booking_revenue) * 100).toFixed(1)
                  : '0'}%
              </div>
              <div className="text-gray-600">Effective Commission Rate</div>
              <div className="text-sm text-gray-500 mt-1">Weighted average across all tiers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {(totals.avg_booking_value || 0).toFixed(2)} KD
              </div>
              <div className="text-gray-600">Revenue per Booking</div>
              <div className="text-sm text-gray-500 mt-1">Average consultation fee</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {((totals.platform_revenue || 0) * 12).toLocaleString(undefined, { maximumFractionDigits: 0 })} KD
              </div>
              <div className="text-gray-600">Projected Annual Platform Revenue</div>
              <div className="text-sm text-gray-500 mt-1">Based on current {timeframe} performance</div>
            </div>
          </div>
        </div>

        {/* Loyalty Points Cost */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6" data-testid="loyalty-report-section">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Loyalty Points</h2>
              <p className="text-sm text-gray-500 mt-1">
                What the {loyalty?.earn_rate_percent || 5}% reward costs EnayaHub against the commission earned.
                Points only cost money when a customer spends them.
              </p>
            </div>
            {loyaltyError && <span className="text-sm text-red-600">{loyaltyError}</span>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
            {[
              {
                label: 'Cost this period',
                value: `${(loyaltyTotals.period_cost_kwd || 0).toFixed(3)} KD`,
                sub: 'Points customers actually spent',
                tone: 'text-rose-600',
                testid: 'loyalty-period-cost',
              },
              {
                label: 'Commission earned',
                value: `${(loyaltyTotals.period_commission_kwd || 0).toFixed(3)} KD`,
                sub: 'Same months, online bookings',
                tone: 'text-green-600',
                testid: 'loyalty-period-commission',
              },
              {
                label: 'Cost of commission',
                value: `${costShare.toFixed(1)}%`,
                sub: 'Points spent ÷ commission',
                tone: costShare > 20 ? 'text-amber-600' : 'text-blue-600',
                testid: 'loyalty-cost-share',
              },
              {
                label: 'Outstanding points',
                value: `${(loyaltyTotals.outstanding_liability_kwd || 0).toFixed(3)} KD`,
                sub: `${(loyaltyTotals.points_outstanding || 0).toLocaleString()} points held by ${loyaltyTotals.members_with_points || 0} customers`,
                tone: 'text-purple-600',
                testid: 'loyalty-outstanding',
              },
            ].map((card) => (
              <div key={card.label} className="rounded-lg bg-gray-50 p-4" data-testid={card.testid}>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{card.label}</p>
                <p className={`text-2xl font-bold mt-1 ${card.tone}`}>{card.value}</p>
                <p className="text-xs text-gray-500 mt-1">{card.sub}</p>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="loyalty-monthly-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="py-2 pe-4">Month</th>
                  <th className="py-2 pe-4 text-right">Points issued</th>
                  <th className="py-2 pe-4 text-right">Points spent</th>
                  <th className="py-2 pe-4 text-right">Cost</th>
                  <th className="py-2 pe-4 text-right">Commission</th>
                  <th className="py-2 pe-4 text-right">Net commission</th>
                  <th className="py-2 text-right">Cost %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loyaltyMonths.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-gray-500">
                      No points activity yet.
                    </td>
                  </tr>
                ) : loyaltyMonths.map((m) => (
                  <tr key={m.month} data-testid={`loyalty-month-${m.month}`}>
                    <td className="py-2.5 pe-4 font-medium text-gray-900">{m.label}</td>
                    <td className="py-2.5 pe-4 text-right text-gray-600">
                      {m.points_issued.toLocaleString()}
                      <span className="text-xs text-gray-400"> ({m.issued_value_kwd.toFixed(3)} KD)</span>
                    </td>
                    <td className="py-2.5 pe-4 text-right text-gray-600">{m.points_redeemed.toLocaleString()}</td>
                    <td className="py-2.5 pe-4 text-right font-semibold text-rose-600">
                      {m.redeemed_cost_kwd > 0 ? `-${m.redeemed_cost_kwd.toFixed(3)}` : '0.000'}
                    </td>
                    <td className="py-2.5 pe-4 text-right text-gray-900">{m.commission_kwd.toFixed(3)}</td>
                    <td className="py-2.5 pe-4 text-right font-semibold text-green-600">{m.net_commission_kwd.toFixed(3)}</td>
                    <td className="py-2.5 text-right text-gray-600">{m.cost_percent_of_commission.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Issued points are a liability, not a cost — the figure in brackets is what they would be worth if every
            customer spent them. Points from cancelled bookings are excluded from both columns.
          </p>
        </div>

        {/* Cancellations & Credits Section */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Cancellations Tracker */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Cancelled Appointments</h3>
                  <p className="text-xs text-gray-500">Tracking cancellations and refunds</p>
                </div>
              </div>
              <div className="bg-red-200 text-red-800 text-sm font-bold px-3 py-1 rounded-full">
                {cancellations.total_cancelled || 0}
              </div>
            </div>
            <div className="p-6">
              {/* Cancellation Stats Grid */}
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-xs text-red-600 font-medium">Refunded as Credit</p>
                  <p className="text-lg font-bold text-red-700">{(cancellations.total_refunded_credit || 0).toFixed(2)} KD</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-xs text-amber-600 font-medium">Pending Refund</p>
                  <p className="text-lg font-bold text-amber-700">{(cancellations.total_pending_refund || 0).toFixed(2)} KD</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 font-medium">Total Lost Revenue</p>
                  <p className="text-lg font-bold text-gray-700">{(cancellations.total_lost_revenue || 0).toFixed(2)} KD</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 font-medium">Cancelled By</p>
                  <div className="flex gap-2 mt-1">
                    {cancellations.cancelled_by && (
                      <>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">User: {cancellations.cancelled_by.patient}</span>
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Provider: {cancellations.cancelled_by.provider}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Recent Cancellations */}
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Recent Cancellations</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(cancellations.recent || []).length > 0 ? (cancellations.recent || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{c.service}</p>
                      <p className="text-xs text-gray-500">{c.user} — {c.provider} — {c.date}</p>
                      {c.reason && <p className="text-xs text-red-500 italic mt-0.5">{c.reason}</p>}
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <p className="font-medium text-gray-900">{c.amount} KD</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        c.refund_status === 'processed' ? 'bg-green-100 text-green-700' :
                        c.refund_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {c.refund_status === 'processed' ? 'Refunded' : c.refund_status === 'pending' ? 'Pending' : 'No refund'}
                      </span>
                    </div>
                  </div>
                )) : (
                  <p className="text-center text-gray-400 py-4 text-sm">No cancellations recorded</p>
                )}
              </div>
            </div>
          </div>

          {/* Credits Tracker */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-blue-50 border-b border-blue-100 px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Credits Issued</h3>
                <p className="text-xs text-gray-500">Refund credits and admin-gifted credits</p>
              </div>
            </div>
            <div className="p-6">
              {/* Credits Overview */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-5 text-white mb-5">
                <p className="text-blue-200 text-sm">Total Credits Issued</p>
                <p className="text-3xl font-bold mt-1">{(credits.total_credits_issued || 0).toFixed(2)} KD</p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Refund Credits</p>
                      <p className="text-xs text-gray-500">{credits.refund_transactions_count || 0} transactions</p>
                    </div>
                  </div>
                  <p className="font-bold text-red-600">{(credits.total_refund_credits || 0).toFixed(2)} KD</p>
                </div>
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Admin Gifted Credits</p>
                      <p className="text-xs text-gray-500">{credits.gift_transactions_count || 0} transactions</p>
                    </div>
                  </div>
                  <p className="font-bold text-green-600">{(credits.total_gifted_credits || 0).toFixed(2)} KD</p>
                </div>
              </div>
            </div>
          </div>

          {/* Wallet Top-Ups */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden xl:col-span-2" data-testid="admin-wallet-topups-card">
            <div className="bg-emerald-50 border-b border-emerald-100 px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Wallet Top-Ups</h3>
                <p className="text-xs text-gray-500">Real money credited into customer & provider wallets (held as platform liability)</p>
              </div>
              {typeof topUps.growth_pct === 'number' && topUps.growth_pct !== 0 && (
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${topUps.growth_pct >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {topUps.growth_pct > 0 ? '+' : ''}{topUps.growth_pct}% this {timeframe}
                </span>
              )}
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-xl p-5 text-white">
                <p className="text-emerald-100 text-sm">Total Topped Up (all time)</p>
                <p className="text-3xl font-bold mt-1" data-testid="admin-wallet-topups-total">{(topUps.total_amount || 0).toFixed(2)} KD</p>
                <p className="text-xs text-emerald-200 mt-1">{topUps.total_count || 0} transactions</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-5">
                <p className="text-emerald-700 text-xs uppercase tracking-wide">This {timeframe}</p>
                <p className="text-2xl font-bold text-emerald-900 mt-1">{(topUps.period_amount || 0).toFixed(2)} KD</p>
                <p className="text-xs text-emerald-700 mt-1">{topUps.period_count || 0} transactions</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-5">
                <p className="text-gray-600 text-xs uppercase tracking-wide mb-2">By channel</p>
                {(topUps.by_channel || []).length === 0 ? (
                  <p className="text-sm text-gray-400">No top-ups yet</p>
                ) : (
                  <div className="space-y-1.5">
                    {topUps.by_channel.map(c => (
                      <div key={c.channel} className="flex items-center justify-between text-sm">
                        <span className="capitalize text-gray-700">{c.channel.replace(/_/g, ' ')}</span>
                        <span className="font-semibold text-gray-900">{c.amount.toFixed(2)} KD <span className="text-xs text-gray-400 font-normal">({c.count})</span></span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {(topUps.recent || []).length > 0 && (
              <div className="border-t border-gray-100 px-6 py-4">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Recent top-ups</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 border-b border-gray-100">
                      <tr>
                        <th className="text-left py-2 font-medium">User</th>
                        <th className="text-left py-2 font-medium">Channel</th>
                        <th className="text-left py-2 font-medium">Date</th>
                        <th className="text-right py-2 font-medium">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topUps.recent.map((t, i) => (
                        <tr key={i} className="border-b border-gray-50 last:border-0">
                          <td className="py-2 text-gray-800">{t.user}</td>
                          <td className="py-2 text-gray-600 capitalize">{(t.channel || '').replace(/_/g, ' ')}</td>
                          <td className="py-2 text-gray-500">{t.date}</td>
                          <td className="py-2 text-right font-semibold text-emerald-700">{t.amount.toFixed(2)} KD</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Detailed Monthly Revenue Table */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Detailed Monthly Revenue</h2>
              <p className="text-sm text-gray-500 mt-1">Last 12 months breakdown</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold text-gray-600">Month</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Bookings</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Cancellations</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Gross Revenue</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Commission</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Refunds</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Top-Ups</th>
                  <th className="text-right px-6 py-3 font-semibold text-gray-600">Net Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {detailedMonthly.map((m, i) => (
                  <tr key={i} className={`hover:bg-gray-50 transition-colors ${m.bookings === 0 && m.cancellations === 0 && (!m.top_ups) ? 'text-gray-400' : ''}`}>
                    <td className="px-6 py-3 font-medium text-gray-900">{m.month}</td>
                    <td className="text-right px-4 py-3">
                      {m.bookings > 0 ? (
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">{m.bookings}</span>
                      ) : '0'}
                    </td>
                    <td className="text-right px-4 py-3">
                      {m.cancellations > 0 ? (
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium">{m.cancellations}</span>
                      ) : '0'}
                    </td>
                    <td className="text-right px-4 py-3 font-medium">{m.gross_revenue > 0 ? `${m.gross_revenue.toFixed(2)} KD` : '—'}</td>
                    <td className="text-right px-4 py-3 text-green-600">{m.commission > 0 ? `${m.commission.toFixed(2)} KD` : '—'}</td>
                    <td className="text-right px-4 py-3 text-red-600">{m.refunds > 0 ? `-${m.refunds.toFixed(2)} KD` : '—'}</td>
                    <td className="text-right px-4 py-3 text-emerald-700">{m.top_ups > 0 ? `${m.top_ups.toFixed(2)} KD` : '—'}</td>
                    <td className="text-right px-6 py-3 font-bold">{m.net_revenue > 0 ? `${m.net_revenue.toFixed(2)} KD` : '—'}</td>
                  </tr>
                ))}
                {/* Totals Row */}
                <tr className="bg-gray-50 font-bold border-t-2 border-gray-300">
                  <td className="px-6 py-3 text-gray-900">Total</td>
                  <td className="text-right px-4 py-3">{detailedMonthly.reduce((s, m) => s + m.bookings, 0)}</td>
                  <td className="text-right px-4 py-3 text-red-600">{detailedMonthly.reduce((s, m) => s + m.cancellations, 0)}</td>
                  <td className="text-right px-4 py-3">{detailedMonthly.reduce((s, m) => s + m.gross_revenue, 0).toFixed(2)} KD</td>
                  <td className="text-right px-4 py-3 text-green-600">{detailedMonthly.reduce((s, m) => s + m.commission, 0).toFixed(2)} KD</td>
                  <td className="text-right px-4 py-3 text-red-600">-{detailedMonthly.reduce((s, m) => s + m.refunds, 0).toFixed(2)} KD</td>
                  <td className="text-right px-4 py-3 text-emerald-700">{detailedMonthly.reduce((s, m) => s + (m.top_ups || 0), 0).toFixed(2)} KD</td>
                  <td className="text-right px-6 py-3">{detailedMonthly.reduce((s, m) => s + m.net_revenue, 0).toFixed(2)} KD</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueDashboard;
